/**
 * This atom handles both groups and users. Separating them in different files causes
 * dependency cycles between the two files when functions have to be imported between them.
 */

import { atom } from "jotai";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  ComponentAccessItem,
  CreateDummyUserPayload,
  FailedNotificationType,
  Group,
  GroupDetailsPayload,
  UpdateGroupPermissionsPayload,
  User,
  AccessType,
  defaultCreateGroupFormFields,
  DummyUserBannerItem,
  DashboardSummary,
  UrsulaUserGraphDataRequest,
  UrsulaUserGraphDataResponse,
} from "shared/models";
import {
  createDummyUser,
  createGroup,
  deleteGroup,
  deleteUser,
  editGroup,
  editUser,
  getComponents,
  getAllGroupsAndUsers,
  getDashboardSummary,
  getUserGraphData,
  getGroupQuotaSummary,
  GroupQuotaSummary,
  getGroupQuotaConfigs,
  GroupQuotaConfig,
  toggleUserStatus,
} from "shared/requests";
import { authTokenAtom, canLoadAppAtom, highestAccessTypeAtom } from "./auth";
import { userPreferencesAtom } from "./user-preferences";

// eslint-disable-next-line import/no-named-as-default-member
dayjs.extend(utc);

//* **************************************************************************************************************** */

/**
 * Primitives
 */

// Atom to handle the visibility of the home page as session is maintained through OIDC
export const showHomePageAtom = atom<boolean>(true);

// Atoms for groups -- start
export const shouldOpenCreateGroupAtom = atom<boolean>(false);
export const showDummyUserAlertAtom = atom<boolean>(false);
export const groupsAtom = atom<Group[]>([]);
export const externalUserCountsAtom = atom<Record<string, number>>({});
// Temporary atom to store group info during creation
export const tempCreateGroupDataAtom = atom<Group>(
  defaultCreateGroupFormFields
);
export const groupsViewModeAtom = atom<string>("grid");
// Atoms for groups -- end

// List of component names the current user can manage/see.
// For global admins this is all components; for techadmins this is their assigned components.
export const availableComponentsAtom = atom<string[]>([]);

// Atoms for users -- start
export const usersAtom = atom<User[]>([]);
export const dummyUsersCreatedAtom = atom<DummyUserBannerItem[]>([]);
export const shouldCloseMenuAtom = atom<boolean>(false);
export const isFilterAlertVisibleAtom = atom<boolean>(false);
// Atoms for users -- end
export const failedNotificationTypeAtom = atom<FailedNotificationType>("none");
// Atom to handle the tab index for the users page
export const usersTabIndexAtom = atom<number>(0);

// Atoms for dashboard summary
export const dashboardSummaryAtom = atom<DashboardSummary | null>(null);
export const dashboardLoadingAtom = atom<boolean>(false);
export const techadminComponentsCountAtom = atom<number>(
  (get) => get(dashboardSummaryAtom)?.techadminComponentsCount ?? 0
);
export const canSelfJoinBillableGroupAtom = atom<boolean>(
  (get) => get(dashboardSummaryAtom)?.canSelfJoinBillableGroup ?? false
);
export const userGraphDataAtom = atom<UrsulaUserGraphDataResponse | null>(null);
export const userGraphDataLoadingAtom = atom<boolean>(false);
// Cache quota summary by billing id to avoid refetching on re-renders.
export const groupQuotaSummaryAtom = atom<Record<string, GroupQuotaSummary>>(
  {}
);
export const groupQuotaSummaryLoadingAtom = atom<boolean>(false);

//* **************************************************************************************************************** */

/**
 * Derived atoms
 *
 */

export const fetchHighestAccessTypeAtom = atom(
  (get) => get(highestAccessTypeAtom),
  async (get, set) => {
    set(dashboardLoadingAtom, true);
    const token = get(authTokenAtom);

    try {
      const summary = await getDashboardSummary(token);
      set(dashboardSummaryAtom, summary);
      set(highestAccessTypeAtom, summary.currentUserAccessType);
      set(canLoadAppAtom, true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      set(dashboardLoadingAtom, false);
    }
  }
);

export const fetchDashboardSummaryAtom = atom(
  (get) => get(dashboardSummaryAtom),
  async (get, set) => {
    set(dashboardLoadingAtom, true);
    const token = get(authTokenAtom);
    try {
      const summary = await getDashboardSummary(token);
      set(dashboardSummaryAtom, summary);
      set(highestAccessTypeAtom, summary.currentUserAccessType);
      set(canLoadAppAtom, true);
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      return false;
    } finally {
      set(dashboardLoadingAtom, false);
    }
  }
);

export const fetchUserGraphDataAtom = atom(
  (get) => get(userGraphDataAtom),
  async (get, set, request?: UrsulaUserGraphDataRequest) => {
    const token = get(authTokenAtom);
    if (!token) return;

    set(userGraphDataLoadingAtom, true);
    try {
      const response = await getUserGraphData(
        {
          // page_number: request?.page_number ?? 1,
          // page_size: request?.page_size ?? 500,
          ...request,
        },
        token
      );
      set(userGraphDataAtom, response);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      set(userGraphDataAtom, null);
    } finally {
      set(userGraphDataLoadingAtom, false);
    }
  }
);

export const fetchGroupQuotaSummaryAtom = atom(
  (get) => get(groupQuotaSummaryAtom),
  async (get, set, billingId?: string) => {
    const token = get(authTokenAtom);
    if (!token || !billingId) return null;

    // Loader shared by all components that request quota summary.
    set(groupQuotaSummaryLoadingAtom, true);
    try {
      const summary = await getGroupQuotaSummary(billingId, token);
      set(groupQuotaSummaryAtom, (prev) => ({ ...prev, [billingId]: summary }));
      return summary;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      return null;
    } finally {
      set(groupQuotaSummaryLoadingAtom, false);
    }
  }
);

// Cache quota configs by billing id
export const groupQuotaConfigsAtom = atom<Record<string, GroupQuotaConfig[]>>(
  {}
);
export const groupQuotaConfigsLoadingAtom = atom<boolean>(false);

export const fetchGroupQuotaConfigsAtom = atom(
  (get) => get(groupQuotaConfigsAtom),
  async (get, set, billingId?: string) => {
    const token = get(authTokenAtom);
    if (!token || !billingId) return null;

    set(groupQuotaConfigsLoadingAtom, true);
    try {
      const configs = await getGroupQuotaConfigs(billingId, token);
      set(groupQuotaConfigsAtom, (prev) => ({ ...prev, [billingId]: configs }));
      return configs;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      return null;
    } finally {
      set(groupQuotaConfigsLoadingAtom, false);
    }
  }
);

// Function to create a group in the database and also update the groups model in the frontend.
export const createGroupInDatabaseAtom = atom(
  null,
  async (get, set, groupData: Group) => {
    const userPreferences = get(userPreferencesAtom);
    const creationDate = new Date();

    // Update envAccess based on ContractType
    const envAccess = groupData.envAccess.map((item) => ({
      ...item,
      value:
        groupData.contractType === "Internal" ? true : item.name === "prod",
    }));

    const nextGroupData: Group = {
      ...groupData,
      ownerId: userPreferences._id,
      creationDate,
      envAccess,
    };

    let groups = get(groupsAtom);
    let users = get(usersAtom);
    const token = get(authTokenAtom);

    try {
      // create a new group and get the created group
      const group = await createGroup(nextGroupData, token);

      // update the atoms
      groups = [group, ...groups];
      users = [...users];
      set(groupsAtom, groups);
      set(usersAtom, users);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      set(failedNotificationTypeAtom, "createGroup");
    }
    // reset temp data to default
    set(tempCreateGroupDataAtom, defaultCreateGroupFormFields);
  }
);

// Function to update the temporary group model with group details info.
export const updateTempWithGroupDetailsAtom = atom(
  null,
  async (get, set, groupDetailsData: GroupDetailsPayload) => {
    // update the temp atom with the group details
    let temp = get(tempCreateGroupDataAtom);
    const previousContractType = temp.contractType;
    temp = { ...temp, ...groupDetailsData };
    if (
      previousContractType &&
      groupDetailsData.contractType &&
      previousContractType !== groupDetailsData.contractType
    ) {
      temp = {
        ...temp,
        componentAccess: temp.componentAccess.map((component) =>
          component.name === "TTS"
            ? { ...component, componentQuotas: undefined }
            : component
        ),
      };
    }
    set(tempCreateGroupDataAtom, temp);
  }
);

// Function to update the temporary group model with group permissions info.
export const updateTempWithGroupPermissionsAtom = atom(
  null,
  async (
    get,
    set,
    {
      groupPermissionsData,
      shouldUpdateDatabase,
    }: UpdateGroupPermissionsPayload
  ) => {
    // update the temp atom with the group details
    let temp = get(tempCreateGroupDataAtom);
    temp = { ...temp, ...groupPermissionsData };
    set(tempCreateGroupDataAtom, temp);

    // update the database
    if (shouldUpdateDatabase) set(createGroupInDatabaseAtom, temp);
  }
);

const updateComponentAccess = (groups: Group[], componentNames: string[]) => {
  const newGroups: Group[] = [];
  const components: string[] = componentNames.length
    ? componentNames
    : (["TTS"] as string[]);
  groups.forEach((group) => {
    const access: ComponentAccessItem[] = [];
    const seen = new Set<string>();
    components.forEach((comp) => {
      const found = group.componentAccess.find(
        (acc) => acc.name === comp && acc.value
      );
      access.push({
        name: comp,
        value: found ? found.value : false,
        sdkAccess: found ? found.sdkAccess : false,
        componentQuotas: found ? found.componentQuotas : undefined,
      });
      seen.add(comp);
    });

    // Preserve any other enabled components that exist on the group but are not part of the
    // "components" list (e.g. legacy components added by admins, or components outside a techadmin's scope).
    // This ensures permission logic can still reason about the full enabled component set for a group.
    group.componentAccess.forEach((acc) => {
      if (acc.value && !seen.has(acc.name)) {
        access.push(acc);
      }
    });
    newGroups.push({ ...group, componentAccess: access });
  });
  return newGroups;
};

// Function to fetch all the groups and users based on the user's access rights.
export const fetchGroupsAndUsersAtom = atom(
  (get) => get(groupsAtom),
  async (get, set) => {
    const wasAppLoadable = get(canLoadAppAtom);
    set(canLoadAppAtom, false);
    // Get the token from the auth token atom
    const token = get(authTokenAtom);
    const userPreferences = get(userPreferencesAtom);
    try {
      const [{ groups, users }, components] = await Promise.all([
        getAllGroupsAndUsers(token),
        getComponents(token).catch(() => []),
      ]);

      const componentNames = components.map((c) => c.name).filter(Boolean);
      set(availableComponentsAtom, componentNames);
      const currentUser = users.find((u) => u._id === userPreferences._id);
      if (currentUser)
        set(
          highestAccessTypeAtom,
          currentUser.groupAccessTypeMapping.find(
            (map) =>
              map.accessType === AccessType.globalAdmin ||
              map.accessType === AccessType.groupAdmin
          )?.accessType || AccessType.viewer
        );

      // Ensure groups are sorted by creationDate (latest first)
      const sortedGroups = groups.sort((a, b) => {
        const dateA = a.creationDate ? new Date(a.creationDate).getTime() : 0;
        const dateB = b.creationDate ? new Date(b.creationDate).getTime() : 0;
        return dateB - dateA;
      });

      const newGroups = updateComponentAccess(sortedGroups, componentNames);
      set(usersAtom, users);
      set(groupsAtom, newGroups);
      set(canLoadAppAtom, true);
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      set(canLoadAppAtom, wasAppLoadable);
      return false;
    }
  }
);

export const deleteGroupAtom = atom(null, async (get, set, groupId: string) => {
  const token = get(authTokenAtom);
  try {
    // remove dummy account alert if group is user's only group
    const dummyUsersCreated = get(dummyUsersCreatedAtom);
    const tempDummyUsers = structuredClone(dummyUsersCreated);

    // filter out all dummy users associated with the deleted group
    const filteredDummyUsers = tempDummyUsers.filter((item) => {
      return !item.user.groupAccessTypeMapping.some(
        (mapping) => mapping.groupId === groupId
      );
    });
    set(dummyUsersCreatedAtom, filteredDummyUsers);

    // if no dummy users remain, hide the dummy user alert
    if (filteredDummyUsers.length === 0) {
      set(showDummyUserAlertAtom, false);
      set(dummyUsersCreatedAtom, []);
    }

    await deleteGroup(groupId, token);
    set(fetchGroupsAndUsersAtom);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
    set(failedNotificationTypeAtom, "deleteGroup");
  }
});

export const editGroupAtom = atom(
  null,
  async (get, set, editedGroup: Group) => {
    const token = get(authTokenAtom);
    // Update the owner of the group to the logged-in user
    const userPreferences = get(userPreferencesAtom);
    editedGroup.ownerId = userPreferences._id;

    try {
      await editGroup(editedGroup, token);
      set(fetchGroupsAndUsersAtom);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      set(failedNotificationTypeAtom, "editGroup");
    }
  }
);

export const createDummyUserAtom = atom(
  null,
  async (
    get,
    set,
    { groupAccessTypeMapping, username }: CreateDummyUserPayload
  ) => {
    try {
      let users = get(usersAtom);
      let dummyUsersCreated = get(dummyUsersCreatedAtom);
      const token = get(authTokenAtom);
      const dummyUser = await createDummyUser(
        groupAccessTypeMapping,
        username,
        token
      );

      users = [...users, dummyUser];
      dummyUsersCreated = [...dummyUsersCreated, { user: dummyUser }];
      set(dummyUsersCreatedAtom, dummyUsersCreated);
      set(usersAtom, users);
      set(showDummyUserAlertAtom, true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error creating dummy user:", error);
      set(failedNotificationTypeAtom, "createDummyUser");
    }
  }
);

export const isLoggedInUserDummyAtom = atom((get) => {
  const userPreferences = get(userPreferencesAtom);
  const users = get(usersAtom);

  // Find the logged-in user in the users array
  const loggedInUser = users.find((user) => user._id === userPreferences._id);

  // Check if the user is a dummy user
  return loggedInUser?.isDummy === true;
});

export const deleteUserAtom = atom(null, async (get, set, userId: string) => {
  const token = get(authTokenAtom);
  const users = get(usersAtom);
  const userToBeDeleted = users.find((u) => u._id === userId);
  try {
    // if the user is dummy then remove user from dummyUsersCreated list
    if (userToBeDeleted && userToBeDeleted.isDummy) {
      const dummyUsersCreated = get(dummyUsersCreatedAtom);
      const tempDummyUsers = structuredClone(dummyUsersCreated);
      dummyUsersCreated.forEach((item, index) => {
        if (item.user._id === userId) {
          tempDummyUsers.splice(index, 1);
        }
      });
      set(dummyUsersCreatedAtom, tempDummyUsers);
      if (tempDummyUsers.length === 0) {
        set(showDummyUserAlertAtom, false);
        set(dummyUsersCreatedAtom, []);
      }
    }
    await deleteUser(userId, token);
    set(fetchGroupsAndUsersAtom);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
    set(failedNotificationTypeAtom, "deleteUser");
  }
});

export const editUserAtom = atom(null, async (get, set, editedUser: User) => {
  const token = get(authTokenAtom);
  try {
    const users = get(usersAtom);
    const responseUser = await editUser(editedUser, token);
    // find the user and overwrite
    const updatedUsers = users.map((user) => {
      if (user._id === responseUser._id) return responseUser;
      return user;
    });
    set(usersAtom, updatedUsers);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
    set(failedNotificationTypeAtom, "editUser");
  }
});

export const toggleUserStatusAtom = atom(
  null,
  async (get, set, params: { userId: string; isActive: boolean }) => {
    const token = get(authTokenAtom);
    try {
      const users = get(usersAtom);
      const responseUser = await toggleUserStatus(
        params.userId,
        params.isActive,
        token
      );
      // find the user and overwrite
      const updatedUsers = users.map((user) => {
        if (user._id === responseUser._id) return responseUser;
        return user;
      });
      set(usersAtom, updatedUsers);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      set(failedNotificationTypeAtom, "editUser");
    }
  }
);
